import { FormatRegistry, type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  CoachConfigurationSnapshotSchema,
  CoachMonthlyCommissionSummarySchema,
  CoachOrderLineSchema,
  CoachQueueEntrySchema,
  CoachServiceRecordSchema,
  CoachShiftSchema,
} from "./coach.js";
import { GroupBuyFulfillmentRecordSchema } from "./group-buy.js";

export * from "./coach.js";
export * from "./commercial.js";
export * from "./group-buy.js";

const DATE_TIME_SEPARATOR = /t/i;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(z|([+-])(\d{2})(?::?(\d{2}))?)$/i;
const DAYS_PER_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isRfc3339DateTime(value: string): boolean {
  const [datePart, timePart, extraPart] = value.split(DATE_TIME_SEPARATOR);
  if (!datePart || !timePart || extraPart) return false;

  const date = DATE.exec(datePart);
  const time = TIME.exec(timePart);
  if (!date || !time) return false;

  const year = Number(date[1]);
  const month = Number(date[2]);
  const day = Number(date[3]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const maxDay = month === 2 && leapYear ? 29 : (DAYS_PER_MONTH[month] ?? 0);
  if (month < 1 || month > 12 || day < 1 || day > maxDay) return false;

  const hour = Number(time[1]);
  const minute = Number(time[2]);
  const second = Number(time[3]);
  const timezoneSign = time[5] === "-" ? -1 : 1;
  const timezoneHour = Number(time[6] ?? 0);
  const timezoneMinute = Number(time[7] ?? 0);
  if (timezoneHour > 23 || timezoneMinute > 59) return false;
  if (hour <= 23 && minute <= 59 && second < 60) return true;

  const utcMinute = minute - timezoneMinute * timezoneSign;
  const utcHour = hour - timezoneHour * timezoneSign - (utcMinute < 0 ? 1 : 0);
  return (
    (utcHour === 23 || utcHour === -1) && (utcMinute === 59 || utcMinute === -1) && second < 61
  );
}

if (!FormatRegistry.Has("date-time")) {
  FormatRegistry.Set("date-time", isRfc3339DateTime);
}

export const EDGE_CONTRACT_VERSION = "1.0.0" as const;
export const EDGE_CONTRACT_MAJOR = 1 as const;
export const HOST_CONTRACT_VERSION = "1.0.0" as const;
export const HOST_CONTRACT_MAJOR = 1 as const;
export const HOST_CONTRACT_V1_PATTERN =
  "^1\\.[0-9]+\\.[0-9]+(?:-[0-9A-Za-z.-]+)?(?:\\+[0-9A-Za-z.-]+)?$" as const;
const HostContractV1VersionSchema = Type.String({ pattern: HOST_CONTRACT_V1_PATTERN });

export const DeploymentShapeSchema = Type.Union([
  Type.Literal("INTEGRATED"),
  Type.Literal("SPLIT"),
]);
export type DeploymentShape = Static<typeof DeploymentShapeSchema>;

export const EdgeNodeBindingSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    componentId: Type.String({ minLength: 1 }),
    credentialId: Type.String({ minLength: 8 }),
    credentialVersion: Type.Integer({ minimum: 1 }),
    deploymentShape: DeploymentShapeSchema,
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type EdgeNodeBinding = Static<typeof EdgeNodeBindingSchema>;

export const RegisterEdgeNodeRequestSchema = Type.Object(
  {
    componentId: Type.String({ minLength: 1 }),
    credentialId: Type.String({ minLength: 8 }),
    deploymentShape: DeploymentShapeSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const MigrateEdgeNodeRequestSchema = Type.Object(
  {
    newComponentId: Type.String({ minLength: 1 }),
    newCredentialId: Type.String({ minLength: 8 }),
    targetShape: DeploymentShapeSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const KioskBindingStatusSchema = Type.Union([
  Type.Literal("UNPAIRED"),
  Type.Literal("ACTIVE"),
  Type.Literal("REVOKED"),
]);
export type KioskBindingStatus = Static<typeof KioskBindingStatusSchema>;

export const TableKioskManagementRecordSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    edgeNodeRegistered: Type.Boolean(),
    deploymentShape: Type.Union([DeploymentShapeSchema, Type.Null()]),
    bindingStatus: KioskBindingStatusSchema,
    componentId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    bindingVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    pairedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    revokedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    pendingComponentId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    pairingExpiresAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type TableKioskManagementRecord = Static<typeof TableKioskManagementRecordSchema>;

export const CreateKioskPairingRequestSchema = Type.Object(
  {
    expectedComponentId: Type.String({ minLength: 8, maxLength: 128 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const CreateKioskPairingResultSchema = Type.Object(
  {
    tableNodeId: Type.String({ minLength: 1 }),
    expectedComponentId: Type.String({ minLength: 8, maxLength: 128 }),
    pairingCode: Type.String({ minLength: 8, maxLength: 16 }),
    expiresAt: Type.String({ format: "date-time" }),
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type CreateKioskPairingResult = Static<typeof CreateKioskPairingResultSchema>;

export const ClaimKioskPairingRequestSchema = Type.Object(
  {
    componentId: Type.String({ minLength: 8, maxLength: 128 }),
    pairingCode: Type.String({ minLength: 8, maxLength: 16 }),
    // New Agents provide a locally derived recovery secret before the Gateway
    // consumes the one-time grant. Optional keeps a safe rolling-upgrade path
    // for existing Agents; only supplied secrets can be replayed after loss.
    bindingSecret: Type.Optional(
      Type.String({ minLength: 43, maxLength: 43, pattern: "^[A-Za-z0-9_-]{43}$" }),
    ),
  },
  { additionalProperties: false },
);

export const KioskBindingClaimResultSchema = Type.Object(
  {
    tableNodeId: Type.String({ minLength: 1 }),
    componentId: Type.String({ minLength: 8, maxLength: 128 }),
    bindingSecret: Type.String({ minLength: 32, maxLength: 128 }),
    bindingVersion: Type.Integer({ minimum: 1 }),
    status: Type.Literal("ACTIVE"),
    pairedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type KioskBindingClaimResult = Static<typeof KioskBindingClaimResultSchema>;

export const KioskBindingDeviceStatusSchema = Type.Object(
  {
    tableNodeId: Type.String({ minLength: 1 }),
    componentId: Type.Union([Type.String({ minLength: 8, maxLength: 128 }), Type.Null()]),
    bindingVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    status: KioskBindingStatusSchema,
    updatedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type KioskBindingDeviceStatus = Static<typeof KioskBindingDeviceStatusSchema>;

export const RevokeKioskBindingRequestSchema = Type.Object(
  {
    reason: Type.String({ minLength: 2, maxLength: 256 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const HostContextSchema = Type.Object(
  {
    contractVersion: HostContractV1VersionSchema,
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    anonymousSessionId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    locale: Type.String({ minLength: 2 }),
    displayRegion: Type.Union([Type.Literal("PRIMARY"), Type.Literal("SECONDARY")]),
    deploymentShape: DeploymentShapeSchema,
  },
  { additionalProperties: true },
);
export type HostContext = Static<typeof HostContextSchema>;

export const AppHealthSchema = Type.Object(
  {
    contractVersion: Type.String({ minLength: 1 }),
    appVersion: Type.String({ minLength: 1 }),
    state: Type.Union([
      Type.Literal("STARTING"),
      Type.Literal("HEALTHY"),
      Type.Literal("DEGRADED"),
      Type.Literal("UNRESPONSIVE"),
      Type.Literal("STOPPED"),
      Type.Literal("INCOMPATIBLE"),
      Type.Literal("CIRCUIT_OPEN"),
    ]),
    cpuPercent: Type.Number({ minimum: 0 }),
    memoryBytes: Type.Integer({ minimum: 0 }),
    pidCount: Type.Integer({ minimum: 0 }),
    openFileCount: Type.Integer({ minimum: 0 }),
    errorCode: Type.Union([Type.String(), Type.Null()]),
    observedAt: Type.String({ format: "date-time" }),
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: true },
);
export type AppHealth = Static<typeof AppHealthSchema>;

export const DisplayIntentSchema = Type.Object(
  {
    contractVersion: HostContractV1VersionSchema,
    intent: Type.Union([Type.Literal("ENTER"), Type.Literal("EXIT")]),
    title: Type.Union([Type.String({ maxLength: 80 }), Type.Null()]),
    requestedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: true },
);
export type DisplayIntent = Static<typeof DisplayIntentSchema>;

export const ShellCommandSchema = Type.Object(
  {
    contractVersion: HostContractV1VersionSchema,
    commandId: Type.String({ minLength: 8 }),
    command: Type.Union([
      Type.Literal("START"),
      Type.Literal("STOP"),
      Type.Literal("ACTIVATE"),
      Type.Literal("BACKGROUND"),
      Type.Literal("HEALTH_CHECK"),
    ]),
    issuedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: true },
);
export type ShellCommand = Static<typeof ShellCommandSchema>;

export function hostContractMajor(version: string): number | null {
  const match =
    /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/.exec(
      version,
    );
  return match ? Number(match[1]) : null;
}

export function isHostContractCompatible(version: string): boolean {
  return hostContractMajor(version) === HOST_CONTRACT_MAJOR;
}

export function isHostContext(value: unknown): value is HostContext {
  return Value.Check(HostContextSchema, value);
}

export function isAppHealth(value: unknown): value is AppHealth {
  return Value.Check(AppHealthSchema, value);
}

export function isShellCommand(value: unknown): value is ShellCommand {
  return Value.Check(ShellCommandSchema, value);
}

export const TableStateSchema = Type.Union([
  Type.Literal("IDLE"),
  Type.Literal("IN_USE"),
  Type.Literal("AWAITING_CLEANING"),
  Type.Literal("DISABLED"),
]);
export type TableState = Static<typeof TableStateSchema>;

export const EvidenceLabelSchema = Type.Union([
  Type.Literal("SIMULATED"),
  Type.Literal("MOCK"),
  Type.Literal("MANUAL_FALLBACK"),
  Type.Literal("SANDBOX"),
  Type.Literal("REAL_HARDWARE"),
  Type.Literal("STORE_ACCEPTED"),
]);
export type EvidenceLabel = Static<typeof EvidenceLabelSchema>;

export const TableSnapshotSchema = Type.Object(
  {
    contractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    area: Type.String({ minLength: 1 }),
    state: TableStateSchema,
    sessionId: Type.Union([Type.String(), Type.Null()]),
    sessionStartedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    billingState: Type.Optional(
      Type.Union([Type.Literal("RUNNING"), Type.Literal("PAUSED"), Type.Literal("ENDED")]),
    ),
    billingPausedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    pricingPlanName: Type.Union([Type.String(), Type.Null()]),
    estimatedFeeFen: Type.Integer({ minimum: 0 }),
    currentTotalFen: Type.Optional(Type.Integer({ minimum: 0 })),
    sessionElapsedSeconds: Type.Optional(Type.Integer({ minimum: 0 })),
    sessionRiskLevel: Type.Optional(
      Type.Union([Type.Literal("NORMAL"), Type.Literal("WARNING"), Type.Literal("CRITICAL")]),
    ),
    longSessionWarningMinutes: Type.Optional(Type.Integer({ minimum: 60, maximum: 100_800 })),
    longSessionCriticalMinutes: Type.Optional(Type.Integer({ minimum: 61, maximum: 100_800 })),
    billingEvidence: EvidenceLabelSchema,
    authoritativeAt: Type.String({ format: "date-time" }),
    revision: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type TableSnapshot = Static<typeof TableSnapshotSchema>;

export const OpenSessionRequestSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type OpenSessionRequest = Static<typeof OpenSessionRequestSchema>;

export const CloseSessionRequestSchema = Type.Object(
  {
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CloseSessionRequest = Static<typeof CloseSessionRequestSchema>;

export const ServiceRequestTypeSchema = Type.Union([
  Type.Literal("SERVICE"),
  Type.Literal("CHECKOUT"),
]);
export type ServiceRequestType = Static<typeof ServiceRequestTypeSchema>;

export const ServiceRequestStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("ACKNOWLEDGED"),
  Type.Literal("COMPLETED"),
  Type.Literal("FAILED"),
]);
export type ServiceRequestStatus = Static<typeof ServiceRequestStatusSchema>;

export const ServiceRequestRecordSchema = Type.Object(
  {
    eventId: Type.String({ minLength: 8 }),
    tableNodeId: Type.String({ minLength: 1 }),
    type: ServiceRequestTypeSchema,
    status: ServiceRequestStatusSchema,
    assignedActorId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    assignedActorDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    requestedAt: Type.String({ format: "date-time" }),
    acknowledgedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type ServiceRequestRecord = Static<typeof ServiceRequestRecordSchema>;

export const ServiceRequestTransitionSchema = Type.Object(
  { idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }) },
  { additionalProperties: false },
);

export const EdgeEventSchema = Type.Object(
  {
    contractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
    eventId: Type.String({ minLength: 8 }),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    deviceId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    bootSessionId: Type.String({ minLength: 1 }),
    deviceSequence: Type.Integer({ minimum: 1 }),
    businessIdempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    type: ServiceRequestTypeSchema,
    occurredAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type EdgeEvent = Static<typeof EdgeEventSchema>;

export const EdgeEventAckSchema = Type.Object({
  eventId: Type.String(),
  accepted: Type.Boolean(),
  duplicate: Type.Boolean(),
  cursor: Type.Integer({ minimum: 1 }),
});
export type EdgeEventAck = Static<typeof EdgeEventAckSchema>;

export const ApiErrorSchema = Type.Object({
  code: Type.String(),
  message: Type.String(),
  requestId: Type.String(),
});
export type ApiError = Static<typeof ApiErrorSchema>;

const PRODUCT_VERSION_PATTERN = "^(0|[1-9]\\d{0,8})\\.(0|[1-9]\\d{0,8})\\.(0|[1-9]\\d{0,8})$";

export const MobileClientPolicySchema = Type.Object(
  {
    latestVersion: Type.String({ pattern: PRODUCT_VERSION_PATTERN }),
    minimumSupportedVersion: Type.String({ pattern: PRODUCT_VERSION_PATTERN }),
  },
  { additionalProperties: false },
);
export type MobileClientPolicy = Static<typeof MobileClientPolicySchema>;

export function compareProductVersions(left: string, right: string): number {
  const parse = (value: string): [number, number, number] => {
    const match = /^(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})$/.exec(value);
    if (!match) throw new Error(`invalid product version: ${value}`);
    return [Number(match[1]), Number(match[2]), Number(match[3])];
  };
  const [leftMajor, leftMinor, leftPatch] = parse(left);
  const [rightMajor, rightMinor, rightPatch] = parse(right);
  const differences = [leftMajor - rightMajor, leftMinor - rightMinor, leftPatch - rightPatch];
  for (const difference of differences) {
    if (difference !== 0) return difference > 0 ? 1 : -1;
  }
  return 0;
}

export const VersionInfoSchema = Type.Object({
  productVersion: Type.String(),
  buildNumber: Type.String(),
  gitCommit: Type.String({ minLength: 3, maxLength: 64 }),
  buildTime: Type.String({ minLength: 1, maxLength: 64 }),
  deploymentEnvironment: Type.String({ minLength: 1, maxLength: 32 }),
  schemaVersion: Type.Integer({ minimum: 0 }),
  edgeContractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
  enabledFeatures: Type.Array(Type.String()),
  evidence: EvidenceLabelSchema,
  mobileClientPolicy: Type.Optional(MobileClientPolicySchema),
});
export type VersionInfo = Static<typeof VersionInfoSchema>;

export const ProductCatalogImportRowSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    unitPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type ProductCatalogImportRow = Static<typeof ProductCatalogImportRowSchema>;

export const ProductCatalogImportRequestSchema = Type.Object(
  {
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    rows: Type.Array(ProductCatalogImportRowSchema, { minItems: 1, maxItems: 500 }),
  },
  { additionalProperties: false },
);
export type ProductCatalogImportRequest = Static<typeof ProductCatalogImportRequestSchema>;

export const ProductCatalogImportResultSchema = Type.Object(
  {
    idempotencyKey: Type.String(),
    imported: Type.Integer({ minimum: 0 }),
    unchanged: Type.Integer({ minimum: 0 }),
    configurationVersion: Type.Integer({ minimum: 1 }),
    appliedAt: Type.String({ format: "date-time" }),
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: false },
);
export type ProductCatalogImportResult = Static<typeof ProductCatalogImportResultSchema>;

export const ConnectivityModeSchema = Type.Union([
  Type.Literal("ONLINE"),
  Type.Literal("OFFLINE"),
  Type.Literal("RECOVERING"),
  Type.Literal("AUTHORITY_EXPIRED"),
]);
export type ConnectivityMode = Static<typeof ConnectivityModeSchema>;

export const SyncStageSchema = Type.Union([
  Type.Literal("IDLE"),
  Type.Literal("UPLOADING_OFFLINE_ORDERS"),
  Type.Literal("RECONCILING_PAYMENTS"),
  Type.Literal("PULLING_CONFIGURATION"),
  Type.Literal("CHECKING_CONFLICTS"),
]);
export type SyncStage = Static<typeof SyncStageSchema>;

export const PaymentMethodSchema = Type.Union([
  Type.Literal("CASH"),
  Type.Literal("STATIC_WECHAT"),
  Type.Literal("STATIC_ALIPAY"),
  Type.Literal("MEMBER_BALANCE"),
]);
export type PaymentMethod = Static<typeof PaymentMethodSchema>;

export const PaymentStatusSchema = Type.Union([
  Type.Literal("CONFIRMED_LOCAL"),
  Type.Literal("PENDING_RECONCILIATION"),
  Type.Literal("MANUALLY_RECONCILED"),
  Type.Literal("RECONCILIATION_EXCEPTION"),
  Type.Literal("PARTIALLY_REFUNDED"),
  Type.Literal("REFUNDED"),
  Type.Literal("CONFIRMED_CLOUD"),
]);
export type PaymentStatus = Static<typeof PaymentStatusSchema>;

export const ConditionalModuleCodeSchema = Type.Union([
  Type.Literal("MEMBER"),
  Type.Literal("GROUP_BUY"),
  Type.Literal("PRODUCT_ORDER"),
  Type.Literal("RESERVATION"),
  Type.Literal("COACH"),
]);
export type ConditionalModuleCode = Static<typeof ConditionalModuleCodeSchema>;

export const PriceTimeBandSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    startMinute: Type.Integer({ minimum: 0, maximum: 1_439 }),
    endMinute: Type.Integer({ minimum: 1, maximum: 1_440 }),
    daysOfWeek: Type.Optional(
      Type.Array(Type.Integer({ minimum: 0, maximum: 6 }), {
        minItems: 1,
        maxItems: 7,
        uniqueItems: true,
      }),
    ),
    hourlyRateFen: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type PriceTimeBand = Static<typeof PriceTimeBandSchema>;

export const PriceSnapshotSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    version: Type.Integer({ minimum: 1 }),
    name: Type.String({ minLength: 1 }),
    hourlyRateFen: Type.Integer({ minimum: 0 }),
    roundingMinutes: Type.Integer({ minimum: 1 }),
    minimumMinutes: Type.Integer({ minimum: 0 }),
    minimumAmountFen: Type.Optional(Type.Integer({ minimum: 0 })),
    billingMode: Type.Optional(Type.Union([Type.Literal("HOURLY"), Type.Literal("PACKAGE")])),
    packageMinutes: Type.Optional(Type.Integer({ minimum: 1, maximum: 10_080 })),
    packagePriceFen: Type.Optional(Type.Integer({ minimum: 0 })),
    roundingMode: Type.Optional(
      Type.Union([Type.Literal("UP"), Type.Literal("NEAREST"), Type.Literal("DOWN")]),
    ),
    roundingScope: Type.Optional(Type.Union([Type.Literal("SESSION"), Type.Literal("SEGMENT")])),
    pauseBillingMode: Type.Optional(Type.Union([Type.Literal("CHARGE"), Type.Literal("FREE")])),
    transferPricingMode: Type.Optional(
      Type.Union([Type.Literal("CONTINUE"), Type.Literal("REPRICE")]),
    ),
    timeZone: Type.Optional(Type.String({ minLength: 1, maxLength: 80 })),
    businessDayCutover: Type.Optional(Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" })),
    timeBands: Type.Optional(Type.Array(PriceTimeBandSchema, { maxItems: 64 })),
    effectiveAt: Type.String({ format: "date-time" }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type PriceSnapshot = Static<typeof PriceSnapshotSchema>;

export const BusinessHoursRuleSchema = Type.Object(
  {
    dayOfWeek: Type.Integer({ minimum: 0, maximum: 6 }),
    closed: Type.Boolean(),
    openTime: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    closeTime: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
  },
  { additionalProperties: false },
);
export type BusinessHoursRule = Static<typeof BusinessHoursRuleSchema>;

export const VenueOperatingSettingsInputSchema = Type.Object(
  {
    storeName: Type.String({ minLength: 1, maxLength: 80 }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    businessHours: Type.Array(BusinessHoursRuleSchema, { minItems: 7, maxItems: 7 }),
    manualPaymentMethods: Type.Object(
      {
        cash: Type.Boolean(),
        staticWechat: Type.Boolean(),
        staticAlipay: Type.Boolean(),
      },
      { additionalProperties: false },
    ),
    longSessionWarningMinutes: Type.Optional(
      Type.Integer({ minimum: 60, maximum: 100_800, default: 720 }),
    ),
    longSessionCriticalMinutes: Type.Optional(
      Type.Integer({ minimum: 61, maximum: 100_800, default: 1_440 }),
    ),
  },
  { additionalProperties: false },
);
export type VenueOperatingSettingsInput = Static<typeof VenueOperatingSettingsInputSchema>;

export const OperatingPricingPlanInputSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    enabled: Type.Boolean(),
    hourlyRateFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    roundingMinutes: Type.Integer({ minimum: 1, maximum: 180 }),
    roundingMode: Type.Union([Type.Literal("UP"), Type.Literal("NEAREST"), Type.Literal("DOWN")]),
    roundingScope: Type.Union([Type.Literal("SESSION"), Type.Literal("SEGMENT")]),
    minimumMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    minimumAmountFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    billingMode: Type.Union([Type.Literal("HOURLY"), Type.Literal("PACKAGE")]),
    packageMinutes: Type.Union([Type.Integer({ minimum: 1, maximum: 10_080 }), Type.Null()]),
    packagePriceFen: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    pauseBillingMode: Type.Union([Type.Literal("CHARGE"), Type.Literal("FREE")]),
    transferPricingMode: Type.Union([Type.Literal("CONTINUE"), Type.Literal("REPRICE")]),
    timeBands: Type.Array(PriceTimeBandSchema, { maxItems: 64 }),
  },
  { additionalProperties: false },
);
export type OperatingPricingPlanInput = Static<typeof OperatingPricingPlanInputSchema>;

export const TableOperatingConfigurationInputSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    tableNumber: Type.String({ minLength: 1, maxLength: 20 }),
    area: Type.String({ minLength: 1, maxLength: 40 }),
    tableType: Type.String({ minLength: 1, maxLength: 40 }),
    tableNodeId: Type.String({ minLength: 1, maxLength: 80 }),
    pricingPlanId: Type.String({ minLength: 1, maxLength: 64 }),
    lightCircuitId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type TableOperatingConfigurationInput = Static<
  typeof TableOperatingConfigurationInputSchema
>;

export const VenueDeviceMappingInputSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 80, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    kind: Type.Union([
      Type.Literal("LIGHT_CONTROLLER"),
      Type.Literal("PRINTER"),
      Type.Literal("TABLE_EDGE_NODE"),
    ]),
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    tableId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    address: Type.String({ minLength: 1, maxLength: 160 }),
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type VenueDeviceMappingInput = Static<typeof VenueDeviceMappingInputSchema>;

export const OperatingConfigurationDraftSchema = Type.Object(
  {
    settings: VenueOperatingSettingsInputSchema,
    pricingPlans: Type.Array(OperatingPricingPlanInputSchema, { minItems: 1, maxItems: 64 }),
    tables: Type.Array(TableOperatingConfigurationInputSchema, { minItems: 1, maxItems: 256 }),
    deviceMappings: Type.Array(VenueDeviceMappingInputSchema, { maxItems: 512 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationDraft = Static<typeof OperatingConfigurationDraftSchema>;

export const OperatingConfigurationPreviewRequestSchema = Type.Object(
  { draft: OperatingConfigurationDraftSchema },
  { additionalProperties: false },
);
export type OperatingConfigurationPreviewRequest = Static<
  typeof OperatingConfigurationPreviewRequestSchema
>;

export const OperatingConfigurationApplyRequestSchema = Type.Object(
  {
    draft: OperatingConfigurationDraftSchema,
    expectedConfigurationVersion: Type.Integer({ minimum: 0 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationApplyRequest = Static<
  typeof OperatingConfigurationApplyRequestSchema
>;

export const OperatingConfigurationIssueSchema = Type.Object(
  {
    path: Type.String({ minLength: 1 }),
    message: Type.String({ minLength: 1 }),
  },
  { additionalProperties: false },
);

export const OperatingConfigurationPreviewResultSchema = Type.Object(
  {
    valid: Type.Boolean(),
    errors: Type.Array(OperatingConfigurationIssueSchema),
    warnings: Type.Array(OperatingConfigurationIssueSchema),
    summary: Type.Array(Type.String()),
    nextConfigurationVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationPreviewResult = Static<
  typeof OperatingConfigurationPreviewResultSchema
>;

export const OperatingConfigurationBundleSchema = Type.Object(
  {
    initialized: Type.Boolean(),
    configurationVersion: Type.Integer({ minimum: 0 }),
    draft: OperatingConfigurationDraftSchema,
    updatedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedBy: Type.Union([Type.String(), Type.Null()]),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationBundle = Static<typeof OperatingConfigurationBundleSchema>;

export const OperatingConfigurationApplyResultSchema = Type.Object(
  {
    configuration: OperatingConfigurationBundleSchema,
    auditId: Type.String({ minLength: 1 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationApplyResult = Static<
  typeof OperatingConfigurationApplyResultSchema
>;

export const BillingSegmentSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 128 }),
    kind: Type.Union([
      Type.Literal("HOURLY"),
      Type.Literal("PACKAGE"),
      Type.Literal("PACKAGE_OVERTIME"),
    ]),
    priceSnapshotId: Type.Optional(Type.String({ minLength: 1 })),
    priceSnapshotVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    priceBandId: Type.String({ minLength: 1, maxLength: 64 }),
    priceBandName: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    startedAt: Type.String({ format: "date-time" }),
    endedAt: Type.String({ format: "date-time" }),
    elapsedSeconds: Type.Integer({ minimum: 0 }),
    billedMinutes: Type.Integer({ minimum: 0 }),
    hourlyRateFen: Type.Integer({ minimum: 0 }),
    roundingMinutes: Type.Integer({ minimum: 1 }),
    roundingMode: Type.Union([
      Type.Literal("UP"),
      Type.Literal("NEAREST"),
      Type.Literal("DOWN"),
      Type.Literal("PACKAGE_FIXED"),
    ]),
    amountFen: Type.Integer({ minimum: 0 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type BillingSegment = Static<typeof BillingSegmentSchema>;

export const BillingBreakdownSchema = Type.Object(
  {
    priceSnapshotId: Type.String({ minLength: 1 }),
    priceSnapshotVersion: Type.Integer({ minimum: 1 }),
    calculatedAt: Type.String({ format: "date-time" }),
    elapsedSeconds: Type.Integer({ minimum: 0 }),
    pausedSeconds: Type.Integer({ minimum: 0 }),
    billableSeconds: Type.Integer({ minimum: 0 }),
    billedMinutes: Type.Integer({ minimum: 0 }),
    subtotalFen: Type.Integer({ minimum: 0 }),
    minimumAmountAdjustmentFen: Type.Integer({ minimum: 0 }),
    totalFen: Type.Integer({ minimum: 0 }),
    segments: Type.Array(BillingSegmentSchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type BillingBreakdown = Static<typeof BillingBreakdownSchema>;

export const ProductSnapshotSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1 }),
    storeProductCode: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    version: Type.Integer({ minimum: 1 }),
    name: Type.String({ minLength: 1 }),
    unitPriceFen: Type.Integer({ minimum: 0 }),
    category: Type.Optional(Type.String({ minLength: 1, maxLength: 40 })),
    unit: Type.Optional(Type.String({ minLength: 1, maxLength: 16 })),
    costFen: Type.Optional(Type.Integer({ minimum: 0 })),
    barcode: Type.Optional(Type.Union([Type.String({ minLength: 4, maxLength: 32 }), Type.Null()])),
    trackInventory: Type.Optional(Type.Boolean()),
    stockOnHand: Type.Optional(Type.Integer()),
    lowStockThreshold: Type.Optional(Type.Integer({ minimum: 0 })),
    allowNegativeStock: Type.Optional(Type.Boolean()),
    enabled: Type.Boolean(),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type ProductSnapshot = Static<typeof ProductSnapshotSchema>;

export const OfflineConfigurationSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    timeZone: Type.String({ minLength: 1 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    version: Type.Integer({ minimum: 1 }),
    effectiveAt: Type.String({ format: "date-time" }),
    selectedConditionalModules: Type.Array(ConditionalModuleCodeSchema, { uniqueItems: true }),
    pricing: PriceSnapshotSchema,
    pricingPlans: Type.Optional(Type.Array(PriceSnapshotSchema)),
    operatingSettings: Type.Optional(VenueOperatingSettingsInputSchema),
    tableConfigurations: Type.Optional(Type.Array(TableOperatingConfigurationInputSchema)),
    deviceMappings: Type.Optional(Type.Array(VenueDeviceMappingInputSchema)),
    products: Type.Array(ProductSnapshotSchema),
    coachConfiguration: Type.Optional(CoachConfigurationSnapshotSchema),
    tables: Type.Array(TableSnapshotSchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OfflineConfiguration = Static<typeof OfflineConfigurationSchema>;

export const BusinessEventTypeSchema = Type.Union([
  Type.Literal("SESSION_OPENED"),
  Type.Literal("SESSION_TRANSFERRED"),
  Type.Literal("SESSION_START_ADJUSTED"),
  Type.Literal("SESSION_PAUSED"),
  Type.Literal("SESSION_RESUMED"),
  Type.Literal("SESSION_ENDED_EARLY"),
  Type.Literal("SESSION_LONG_RUNNING_RESOLVED"),
  Type.Literal("SESSION_VOIDED"),
  Type.Literal("ORDER_ITEM_ADDED"),
  Type.Literal("ORDER_ITEM_RETURNED"),
  Type.Literal("SESSION_CHECKED_OUT"),
  Type.Literal("TABLE_CLEARED"),
  Type.Literal("PAYMENT_REFUNDED"),
  Type.Literal("PAYMENT_RECONCILED"),
  Type.Literal("COACH_SHIFT_CHANGED"),
  Type.Literal("COACH_QUEUE_CHANGED"),
  Type.Literal("COACH_SERVICE_CHANGED"),
  Type.Literal("COACH_SERVICE_ADJUSTED"),
  Type.Literal("COACH_COMMISSION_MONTH_SETTLED"),
  Type.Literal("GROUP_BUY_FULFILLED"),
  Type.Literal("GROUP_BUY_RECONCILED"),
]);
export type BusinessEventType = Static<typeof BusinessEventTypeSchema>;

export const GatewayLeaseSchema = Type.Object(
  {
    leaseId: Type.String({ minLength: 1 }),
    gatewayId: Type.String({ minLength: 1 }),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    fencingToken: Type.Integer({ minimum: 1 }),
    issuedAt: Type.String({ format: "date-time" }),
    expiresAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type GatewayLease = Static<typeof GatewayLeaseSchema>;

export const SyncEventResultSchema = Type.Object(
  {
    eventId: Type.String(),
    status: Type.Union([
      Type.Literal("ACCEPTED"),
      Type.Literal("DUPLICATE"),
      Type.Literal("OUT_OF_ORDER"),
      Type.Literal("CONFLICT"),
      Type.Literal("REJECTED"),
    ]),
    code: Type.Union([Type.String(), Type.Null()]),
    message: Type.Union([Type.String(), Type.Null()]),
  },
  { additionalProperties: false },
);
export type SyncEventResult = Static<typeof SyncEventResultSchema>;

export const SyncBatchResponseSchema = Type.Object(
  { results: Type.Array(SyncEventResultSchema), receivedAt: Type.String({ format: "date-time" }) },
  { additionalProperties: false },
);
export type SyncBatchResponse = Static<typeof SyncBatchResponseSchema>;

export const OrderItemRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    skuId: Type.String({ minLength: 1 }),
    name: Type.String({ minLength: 1 }),
    unitPriceFen: Type.Integer({ minimum: 0 }),
    unitCostFen: Type.Optional(Type.Integer({ minimum: 0 })),
    costSnapshotKnown: Type.Optional(Type.Boolean()),
    quantity: Type.Integer({ minimum: 1 }),
    returnedQuantity: Type.Integer({ minimum: 0 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OrderItemRecord = Static<typeof OrderItemRecordSchema>;

export const PaymentRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    method: PaymentMethodSchema,
    amountFen: Type.Integer({ minimum: 0 }),
    status: PaymentStatusSchema,
    evidence: Type.Union([Type.Literal("MANUAL_FALLBACK"), Type.Literal("STORE_ACCEPTED")]),
    recordedAt: Type.String({ format: "date-time" }),
    offlineOrigin: Type.Boolean(),
    memberId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    referenceId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
  },
  { additionalProperties: false },
);
export type PaymentRecord = Static<typeof PaymentRecordSchema>;

export const RefundRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    paymentId: Type.String({ minLength: 1 }),
    amountFen: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    evidence: Type.Literal("MANUAL_FALLBACK"),
    actorId: Type.String({ minLength: 1 }),
    authorizerId: Type.String({ minLength: 1 }),
    createdAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type RefundRecord = Static<typeof RefundRecordSchema>;

export const CashShiftOpenTableSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    sessionId: Type.String({ minLength: 1 }),
    startedAt: Type.String({ format: "date-time" }),
    currentAmountFen: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type CashShiftOpenTable = Static<typeof CashShiftOpenTableSchema>;

export const CashShiftInventoryDifferenceSchema = Type.Object(
  {
    movementId: Type.String({ minLength: 1 }),
    skuId: Type.String({ minLength: 1 }),
    productName: Type.String({ minLength: 1 }),
    quantityBefore: Type.Integer(),
    quantityAfter: Type.Integer(),
    quantityDelta: Type.Integer(),
    reason: Type.String({ minLength: 1 }),
    actorId: Type.String({ minLength: 1 }),
    occurredAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CashShiftInventoryDifference = Static<typeof CashShiftInventoryDifferenceSchema>;

export const CashShiftTimelineEntrySchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    type: Type.Union([
      Type.Literal("STARTED"),
      Type.Literal("HANDOVER_SUBMITTED"),
      Type.Literal("RECEIVED"),
    ]),
    actorId: Type.String({ minLength: 1 }),
    actorDisplayName: Type.String({ minLength: 1 }),
    occurredAt: Type.String({ format: "date-time" }),
    note: Type.String(),
  },
  { additionalProperties: false },
);
export type CashShiftTimelineEntry = Static<typeof CashShiftTimelineEntrySchema>;

export const CashShiftRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: Type.Union([
      Type.Literal("OPEN"),
      Type.Literal("HANDOVER_PENDING"),
      Type.Literal("CLOSED"),
    ]),
    openedBy: Type.String({ minLength: 1 }),
    openedByDisplayName: Type.String({ minLength: 1 }),
    openingCashFen: Type.Integer({ minimum: 0 }),
    startNote: Type.String(),
    startedAt: Type.String({ format: "date-time" }),
    cashReceiptsFen: Type.Integer({ minimum: 0 }),
    cashRefundsFen: Type.Integer({ minimum: 0 }),
    expectedCashFen: Type.Integer(),
    countedCashFen: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    cashDifferenceFen: Type.Union([Type.Integer(), Type.Null()]),
    exceptionNote: Type.String(),
    openTables: Type.Array(CashShiftOpenTableSchema),
    inventoryDifferences: Type.Array(CashShiftInventoryDifferenceSchema),
    handoverSubmittedBy: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    handoverSubmittedByDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    handoverSubmittedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    receivedBy: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    receivedByDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    receivedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    receiveNote: Type.String(),
    version: Type.Integer({ minimum: 1 }),
    timeline: Type.Array(CashShiftTimelineEntrySchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type CashShiftRecord = Static<typeof CashShiftRecordSchema>;

export const OrderDetailSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    sessionId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: Type.Union([
      Type.Literal("OPEN"),
      Type.Literal("SETTLED"),
      Type.Literal("REFUNDED"),
      Type.Literal("CONFLICT"),
    ]),
    priceSnapshot: PriceSnapshotSchema,
    priceSnapshots: Type.Optional(Type.Array(PriceSnapshotSchema, { minItems: 1 })),
    billingBreakdown: Type.Optional(BillingBreakdownSchema),
    billingState: Type.Optional(
      Type.Union([Type.Literal("RUNNING"), Type.Literal("PAUSED"), Type.Literal("ENDED")]),
    ),
    billingPausedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    voidedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    voidReason: Type.Optional(Type.Union([Type.String({ minLength: 2 }), Type.Null()])),
    startedAt: Type.String({ format: "date-time" }),
    endedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    tableFeeFen: Type.Integer({ minimum: 0 }),
    productTotalFen: Type.Integer({ minimum: 0 }),
    coachServiceTotalFen: Type.Integer(),
    totalFen: Type.Integer({ minimum: 0 }),
    paidFen: Type.Integer({ minimum: 0 }),
    refundedFen: Type.Integer({ minimum: 0 }),
    items: Type.Array(OrderItemRecordSchema),
    coachServiceItems: Type.Array(CoachOrderLineSchema),
    groupBuyFulfillments: Type.Optional(Type.Array(GroupBuyFulfillmentRecordSchema)),
    payments: Type.Array(PaymentRecordSchema),
    refunds: Type.Array(RefundRecordSchema),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("SYNCED"),
      Type.Literal("CONFLICT"),
      Type.Literal("FAILED"),
      Type.Literal("ISOLATED"),
    ]),
  },
  { additionalProperties: false },
);
export type OrderDetail = Static<typeof OrderDetailSchema>;

export const ManualReviewItemSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    type: Type.Union([
      Type.Literal("PAYMENT"),
      Type.Literal("ORDER_CONFLICT"),
      Type.Literal("COACH_CONFLICT"),
    ]),
    objectId: Type.String({ minLength: 1 }),
    orderId: Type.String({ minLength: 1 }),
    reasonCode: Type.String({ minLength: 1 }),
    title: Type.String({ minLength: 1 }),
    detail: Type.String({ minLength: 1 }),
    amountFen: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    status: Type.Union([Type.Literal("PENDING"), Type.Literal("RESOLVED")]),
    createdAt: Type.String({ format: "date-time" }),
    resolvedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    offlineOrigin: Type.Boolean(),
    businessContext: Type.Optional(
      Type.Object(
        {
          eventType: Type.String({ minLength: 1 }),
          eventLabel: Type.String({ minLength: 1 }),
          tableNumber: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          sessionId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          orderId: Type.String({ minLength: 1 }),
          businessDate: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          occurredAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
          synchronizedAt: Type.String({ format: "date-time" }),
          actorId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          difference: Type.String({ minLength: 1 }),
          recommendedAction: Type.String({ minLength: 1 }),
        },
        { additionalProperties: false },
      ),
    ),
    resolutionDisposition: Type.Optional(
      Type.Union([
        Type.Literal("KEEP_ISOLATED"),
        Type.Literal("TRANSFER_TO_FINANCE"),
        Type.Literal("TRANSFER_TO_OPERATIONS"),
      ]),
    ),
  },
  { additionalProperties: false },
);
export type ManualReviewItem = Static<typeof ManualReviewItemSchema>;

export function isTableSnapshot(value: unknown): value is TableSnapshot {
  return Value.Check(TableSnapshotSchema, value);
}

export function isOrderDetail(value: unknown): value is OrderDetail {
  return Value.Check(OrderDetailSchema, value);
}

export function isManualReviewItem(value: unknown): value is ManualReviewItem {
  return Value.Check(ManualReviewItemSchema, value);
}

const BusinessEventEnvelopeFields = {
  contractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
  eventId: Type.String({ minLength: 8 }),
  tenantId: Type.String({ minLength: 1 }),
  storeId: Type.String({ minLength: 1 }),
  gatewayId: Type.String({ minLength: 1 }),
  fencingToken: Type.Integer({ minimum: 1 }),
  gatewaySequence: Type.Integer({ minimum: 1 }),
  businessId: Type.String({ minLength: 1 }),
  idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  aggregateId: Type.String({ minLength: 1 }),
  aggregateVersion: Type.Integer({ minimum: 1 }),
  occurredAt: Type.String({ format: "date-time" }),
  offlineOrigin: Type.Boolean(),
  actorId: Type.String({ minLength: 1 }),
  authorizerId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
};

const BusinessProjectionPayloadFields = {
  order: OrderDetailSchema,
  tableSnapshot: TableSnapshotSchema,
  tableSnapshots: Type.Array(TableSnapshotSchema, { minItems: 1 }),
};

const ProjectionPayloadSchema = Type.Object(BusinessProjectionPayloadFields, {
  additionalProperties: false,
});

export const BusinessEventSchema = Type.Union([
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("GROUP_BUY_FULFILLED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          fulfillment: GroupBuyFulfillmentRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("GROUP_BUY_RECONCILED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          fulfillment: GroupBuyFulfillmentRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_OPENED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_TRANSFERRED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Union([
        Type.Literal("SESSION_START_ADJUSTED"),
        Type.Literal("SESSION_PAUSED"),
        Type.Literal("SESSION_RESUMED"),
        Type.Literal("SESSION_ENDED_EARLY"),
        Type.Literal("SESSION_LONG_RUNNING_RESOLVED"),
        Type.Literal("SESSION_VOIDED"),
      ]),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("ORDER_ITEM_ADDED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("ORDER_ITEM_RETURNED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          reason: Type.String({ minLength: 2, maxLength: 200 }),
          quantity: Type.Integer({ minimum: 1, maximum: 99 }),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_CHECKED_OUT"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          manualReview: Type.Union([ManualReviewItemSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("TABLE_CLEARED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PAYMENT_REFUNDED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          refund: RefundRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PAYMENT_RECONCILED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          manualReview: ManualReviewItemSchema,
          outcome: Type.Union([
            Type.Literal("MANUALLY_RECONCILED"),
            Type.Literal("NOT_RECEIVED"),
            Type.Literal("AMOUNT_MISMATCH"),
            Type.Literal("DUPLICATE_PAYMENT"),
          ]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
]);
export type BusinessEvent = Static<typeof BusinessEventSchema>;

export const CoachBusinessEventSchema = Type.Union([
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SHIFT_CHANGED"),
      payload: Type.Object(
        {
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_QUEUE_CHANGED"),
      payload: Type.Object(
        {
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SERVICE_CHANGED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
          coachService: CoachServiceRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SERVICE_ADJUSTED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
          coachService: CoachServiceRecordSchema,
          refund: Type.Union([RefundRecordSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_COMMISSION_MONTH_SETTLED"),
      payload: Type.Object(
        {
          monthlyCommission: CoachMonthlyCommissionSummarySchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
]);
export type CoachBusinessEvent = Static<typeof CoachBusinessEventSchema>;

export function isCoachBusinessEvent(value: unknown): value is CoachBusinessEvent {
  return Value.Check(CoachBusinessEventSchema, value);
}

export const BusinessSyncEventSchema = Type.Union([BusinessEventSchema, CoachBusinessEventSchema]);
export type BusinessSyncEvent = Static<typeof BusinessSyncEventSchema>;

// The transport validates the trusted envelope first; each payload is then validated and
// isolated per event so one damaged record cannot block later gateway sequences in the batch.
export const BusinessEventTransportSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: BusinessEventTypeSchema,
    payload: Type.Record(Type.String(), Type.Unknown()),
  },
  { additionalProperties: false },
);

export const SyncBatchRequestSchema = Type.Object(
  {
    gatewayId: Type.String({ minLength: 1 }),
    events: Type.Array(BusinessEventTransportSchema, { maxItems: 100 }),
  },
  { additionalProperties: false },
);
export type SyncBatchRequest = Static<typeof SyncBatchRequestSchema>;

export interface GatewayStatus {
  mode: ConnectivityMode;
  syncStage: SyncStage;
  gatewayId: string;
  fencingToken: number | null;
  leaseExpiresAt: string | null;
  offlineAuthorityAvailable: boolean;
  offlineSince: string | null;
  offlineDurationSeconds: number;
  lastSuccessfulSyncAt: string | null;
  pendingRecords: number;
  failedRecords: number;
  conflictRecords: number;
  pendingReconciliationCount: number;
  selectedConditionalModules: ConditionalModuleCode[];
  manualPaymentMethods: VenueOperatingSettingsInput["manualPaymentMethods"];
  simulationAllowed: boolean;
  serviceHealth: {
    cloudSync: boolean;
    onlineRefund: boolean;
    paymentReconciliation: boolean;
    channelPaymentConfirmation: boolean;
    vouchers: boolean;
    memberFunds: boolean;
  };
  syncProgress: { processed: number; total: number; remaining: number; failed: number };
  availableFeatures: Array<{ code: string; label: string }>;
  unavailableFeatures: Array<{ code: string; label: string; reason: string }>;
  lastRecoverySummary: {
    synced: number;
    failed: number;
    pendingReconciliation: number;
    conflicts: number;
    restoredFeatures: string[];
    completedAt: string;
  } | null;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export interface SystemComponentStatus {
  component: "ops-web" | "cloud-api" | "venue-edge-gateway" | "table-edge-agent";
  productVersion: string;
  buildNumber: string;
  schemaVersion: number;
  contractVersion: string;
  health: "HEALTHY" | "DEGRADED" | "UNREACHABLE";
  evidence: EvidenceLabel;
}

export interface SystemStatus {
  generatedAt: string;
  overall: "HEALTHY" | "DEGRADED";
  components: SystemComponentStatus[];
  gateway: GatewayStatus;
  storage: {
    tables: number;
    orders: number;
    openSessions: number;
    operatorAccounts: number;
    coachProfiles: number;
    activeCoachShifts: number;
    coachQueueEntries: number;
    coachServices: number;
    activeCoachServices: number;
    pendingOutbox: number;
    failedOutbox: number;
    pendingManualReview: number;
    auditRecords: number;
    integrity: "OK" | "FAILED";
  };
  operationalEvidence: "SIMULATED";
}

export interface DiagnosticBundle {
  format: "billiard-erp-diagnostic-v1";
  generatedAt: string;
  redaction: {
    policy: "STRICT";
    containsSecrets: false;
    identifiers: "SHA256_PREFIX";
  };
  scope: { tenantHash: string; storeHash: string; gatewayHash: string };
  system: SystemStatus;
  recentFailures: Array<{
    category: string;
    count: number;
    lastOccurredAt: string | null;
  }>;
  excluded: string[];
  evidence: "SIMULATED";
}

export const TransferSessionRequestSchema = Type.Object(
  {
    targetTableId: Type.String({ minLength: 1 }),
    reason: Type.Optional(Type.String({ minLength: 2, maxLength: 200 })),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const AdjustSessionStartRequestSchema = Type.Object(
  {
    startedAt: Type.String({ format: "date-time" }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const SessionBillingActionRequestSchema = Type.Object(
  {
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const DeviceEffectActionRequestSchema = Type.Object(
  { idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }) },
  { additionalProperties: false },
);

export const AddOrderItemRequestSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const ReturnOrderItemRequestSchema = Type.Object(
  {
    itemId: Type.String({ minLength: 1 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const CheckoutRequestSchema = Type.Object(
  {
    method: PaymentMethodSchema,
    memberId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    memberBalanceFen: Type.Optional(Type.Integer({ minimum: 0 })),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const RefundRequestSchema = Type.Object(
  {
    amountFen: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const ReconcilePaymentRequestSchema = Type.Object(
  {
    outcome: Type.Union([
      Type.Literal("MANUALLY_RECONCILED"),
      Type.Literal("NOT_RECEIVED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_PAYMENT"),
    ]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
